<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Data table primitive with sorting, filtering, selection, and pagination.
 *
 * @status internal
 * @group lists-tables
 * @slot body-cell-[column] Custom cell renderer; receives Quasar row slot props { row, col, value, ... }.
 * @slot header-cell-[column] Custom header cell for the named column; receives Quasar header slot props.
 * @slot header-selection Whole select-all control in the header; receives Quasar's header slot props.
 * @slot body-selection Whole select control for one row; receives Quasar's row slot props.
 * @slot body Whole body row — nested or grouped rows. Owning it drops the cells and every `body-cell-*` slot.
 * @slot header Whole header row. Owning it drops every `header-cell-*` slot.
 * @slot top-left Toolbar left area — search fields, filter chips, or other table-level controls.
 * @slot top-right Toolbar right area — bulk actions, or a table-level control such as a column picker.
 * @slot bottom Custom footer content replacing the default pagination bar.
 * @slot no-data Content shown when `rows` is empty.
 * @see https://quasar.dev/vue-components/table Quasar q-table API
 */
defineOptions({ name: 'HrTable' });

const props = defineProps({
  /** Array of row data objects to display. */
  rows: {
    type: Array,
    default: () => [],
  },
  /** Column definition objects following the Quasar QTable column schema. */
  columns: {
    type: Array,
    default: () => [],
  },
  /** Property name on each row object used as the unique row identifier. */
  rowKey: {
    type: String,
    default: 'id',
  },
  /** When true, shows a loading spinner overlay on the table body. */
  loading: {
    type: Boolean,
    default: false,
  },
  /** Text shown when `rows` is empty. */
  noDataLabel: {
    type: String,
    default: undefined,
  },
  /** Text shown when a filter/search produces no results. */
  noResultsLabel: {
    type: String,
    default: undefined,
  },
  /** Allowed page-size choices in the pagination footer. Pass `[0]` to disable pagination. */
  rowsPerPageOptions: {
    type: Array,
    default: undefined,
  },
  /**
   * Row selection mode.
   * @values single, multiple, none
   */
  selection: {
    type: String,
    default: 'none',
    validator: (v) => ['single', 'multiple', 'none'].includes(v),
  },
  /**
   * Row/column divider style. M3 tables use horizontal row dividers by default.
   * @values horizontal, vertical, cell, none
   */
  separator: {
    type: String,
    default: 'horizontal',
    validator: (v) => ['horizontal', 'vertical', 'cell', 'none'].includes(v),
  },
  /** String or object passed to the built-in filter. When a string, rows are filtered by matching any column value. */
  filter: {
    type: [String, Object],
    default: undefined,
  },
  /** Custom filter function `(rows, terms, cols, getCellValue) => rows`. Overrides the built-in filter. */
  filterMethod: {
    type: Function,
    default: undefined,
  },
  /** Array of column `name` values controlling which columns are rendered. All columns shown when undefined. */
  visibleColumns: {
    type: Array,
    default: undefined,
  },
  /** Table caption text rendered in the top toolbar. */
  title: {
    type: String,
    default: undefined,
  },
  /** When true, cell content wraps instead of being truncated. */
  wrapCells: {
    type: Boolean,
    default: false,
  },
  /** When true, column sort cycles between ascending and descending only (skips unsorted). */
  binaryStateSort: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => ({
  rows: props.rows,
  columns: props.columns,
  rowKey: props.rowKey,
  loading: props.loading,
  ...(props.noDataLabel !== undefined && { noDataLabel: props.noDataLabel }),
  ...(props.noResultsLabel !== undefined && { noResultsLabel: props.noResultsLabel }),
  ...(props.rowsPerPageOptions !== undefined && { rowsPerPageOptions: props.rowsPerPageOptions }),
  selection: props.selection,
  separator: props.separator,
  ...(props.filter !== undefined && { filter: props.filter }),
  ...(props.filterMethod !== undefined && { filterMethod: props.filterMethod }),
  ...(props.visibleColumns !== undefined && { visibleColumns: props.visibleColumns }),
  ...(props.title !== undefined && { title: props.title }),
  wrapCells: props.wrapCells,
  binaryStateSort: props.binaryStateSort,
  ...(hasTheme.value ? {} : props.legacyProps),
  ...attrs,
  flat: true,
}));
</script>
<template>
  <div class="hr-table">
    <q-table v-bind="effectiveProps">
      <template
        v-if="$slots['top-left']"
        #top-left="slotProps"
      >
        <slot
          name="top-left"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['top-right']"
        #top-right="slotProps"
      >
        <slot
          name="top-right"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots.bottom"
        #bottom="slotProps"
      >
        <slot
          name="bottom"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['no-data']"
        #no-data="slotProps"
      >
        <slot
          name="no-data"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots.header"
        #header="slotProps"
      >
        <slot
          name="header"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots.body"
        #body="slotProps"
      >
        <slot
          name="body"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['header-selection']"
        #header-selection="slotProps"
      >
        <slot
          name="header-selection"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-if="$slots['body-selection']"
        #body-selection="slotProps"
      >
        <slot
          name="body-selection"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-for="col in props.columns.filter(c => $slots[`body-cell-${c.name}`])"
        :key="`body-cell-${col.name}`"
        #[`body-cell-${col.name}`]="slotProps"
      >
        <slot
          :name="`body-cell-${col.name}`"
          v-bind="slotProps ?? {}"
        />
      </template>
      <template
        v-for="col in props.columns.filter(c => $slots[`header-cell-${c.name}`])"
        :key="`header-cell-${col.name}`"
        #[`header-cell-${col.name}`]="slotProps"
      >
        <slot
          :name="`header-cell-${col.name}`"
          v-bind="slotProps ?? {}"
        />
      </template>
    </q-table>
  </div>
</template>
<style lang="scss" scoped>
/* Reset Quasar's hardcoded colors so the parent surface shows through */
.hr-table :deep(.q-table__container),
.hr-table :deep(.q-table),
.hr-table :deep(.q-table__top),
.hr-table :deep(.q-table__bottom) {
  background: transparent;
  color: inherit;
}

.hr-table :deep(.q-table thead),
.hr-table :deep(.q-table tr),
.hr-table :deep(.q-table th),
.hr-table :deep(.q-table td) {
  border-color: var(--app-outline-variant);
}

/*
 * Quasar tints hovered and selected rows black, and only a QTable told it is dark switches to white.
 * Our dark theme never tells it, so both tints vanished on the dark surface. Fainter than the usual
 * 8% state layer, because a row must stay lighter than the header's surface-container band.
 */
[data-theme] .hr-table :deep(.q-table tbody td::before) {
  background: color-mix(in srgb, var(--app-on-surface) 3%, transparent);
}

[data-theme] .hr-table :deep(.q-table tbody td::after) {
  background: color-mix(in srgb, var(--app-on-surface) 6%, transparent);
}

.hr-table :deep(.q-table thead th) {
  font-size: var(--app-type-title-small-size);
  line-height: var(--app-type-title-small-line-height);
  font-weight: var(--app-type-title-small-weight);
  letter-spacing: var(--app-type-title-small-tracking);
}

/* Quasar's 13px is off the M3 scale. Themed only, so the legacy layouts keep the size their users know. */
[data-theme] .hr-table :deep(.q-table tbody td) {
  font-size: var(--app-type-body-medium-size);
  line-height: var(--app-type-body-medium-line-height);
  font-weight: var(--app-type-body-medium-weight);
  letter-spacing: var(--app-type-body-medium-tracking);
}

.hr-table :deep(.q-table__bottom *),
.hr-table :deep(.q-table__bottom .q-field .q-field__native),
.hr-table :deep(.q-table__bottom .q-field .q-field__marginal),
.hr-table :deep(.q-table__bottom .q-field .q-field__control) {
  color: inherit;
}
</style>
